import React, { useState, useEffect } from 'react';

const ImageWithFallback = ({ 
  src, 
  fallbackSrc, 
  fallbackType,
  index = 0,
  alt, 
  className = "", 
  onError = null,
  ...props 
}) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  // Fonction pour obtenir l'image de fallback selon le type
  const getFallbackImage = (type, index = 0) => {
    switch(type) {
      case 'vatican': 
        const vaticanImages = [DEFAULT_IMAGES.pope1, DEFAULT_IMAGES.vatican1, DEFAULT_IMAGES.pope2, DEFAULT_IMAGES.vatican2, DEFAULT_IMAGES.pope3, DEFAULT_IMAGES.vatican3];
        return vaticanImages[index % vaticanImages.length];
      case 'pope': 
        const popeImages = [DEFAULT_IMAGES.pope1, DEFAULT_IMAGES.pope2, DEFAULT_IMAGES.pope3, DEFAULT_IMAGES.pope4];
        return popeImages[index % popeImages.length];
      case 'church': return DEFAULT_IMAGES.church1;
      case 'saint': return DEFAULT_IMAGES.saint1;
      case 'bible': return DEFAULT_IMAGES.bible1;
      case 'couple': 
        const coupleImages = [DEFAULT_IMAGES.couple1, DEFAULT_IMAGES.couple2, DEFAULT_IMAGES.couple3, DEFAULT_IMAGES.wedding1, DEFAULT_IMAGES.wedding2];
        return coupleImages[index % coupleImages.length];
      case 'family': 
        const familyImages = [DEFAULT_IMAGES.family1, DEFAULT_IMAGES.family2, DEFAULT_IMAGES.family3, DEFAULT_IMAGES.family4, DEFAULT_IMAGES.family5];
        return familyImages[index % familyImages.length];
      case 'phone': return DEFAULT_IMAGES.phone;
      case 'prayer': return DEFAULT_IMAGES.prayer1;
      default: return DEFAULT_IMAGES.defaultLandscape;
    }
  };

  // UseEffect pour gérer les images vides dès le début
  useEffect(() => {
    if (!src || src.trim() === '') {
      setHasError(true);
      const defaultImg = fallbackSrc || getFallbackImage(fallbackType, index || 0);
      setImgSrc(defaultImg);
    } else {
      setImgSrc(src);
      setHasError(false);
    }
  }, [src, fallbackSrc, fallbackType, index]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      const defaultImg = fallbackSrc || getFallbackImage(fallbackType, index || 0);
      setImgSrc(defaultImg);
    }
    if (onError) onError();
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
      loading="lazy"
      {...props}
    />
  );
};

// Images par défaut de droits libres (Unsplash)
export const DEFAULT_IMAGES = {
  // Couples chrétiens et mariages africains
  couple1: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=600&fit=crop&crop=faces",
  couple2: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=600&fit=crop&crop=center",
  couple3: "https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=400&h=600&fit=crop&crop=faces",
  wedding1: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=600&fit=crop&crop=center",
  wedding2: "https://images.unsplash.com/photo-1529636798458-92182e662485?w=400&h=600&fit=crop&crop=center",
  
  // Familles africaines
  family1: "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=400&h=600&fit=crop&crop=faces",
  family2: "https://images.unsplash.com/photo-1612892483236-52d32a0e0ac1?w=400&h=600&fit=crop&crop=faces",
  family3: "https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?w=400&h=600&fit=crop&crop=faces",
  family4: "https://images.unsplash.com/photo-1543699936-c582ac6e7bc1?w=400&h=600&fit=crop&crop=faces",
  family5: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop&crop=faces",
  
  // Églises et spiritualité
  church1: "https://images.unsplash.com/photo-1520637836862-4d197d17c93a?w=400&h=600&fit=crop&crop=center",
  church2: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop&crop=center",
  church3: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop&crop=center",
  
  // Prière et méditation
  prayer1: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop&crop=center",
  prayer2: "https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=400&h=600&fit=crop&crop=center",
  prayer3: "https://images.unsplash.com/photo-1507692049790-de58290a4334?w=400&h=600&fit=crop&crop=center",
  
  // Saints et personnages religieux
  saint1: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=400&fit=crop&crop=center",
  saint2: "https://images.unsplash.com/photo-1520637736862-4d197d17c93a?w=300&h=400&fit=crop&crop=center",
  saint3: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=400&fit=crop&crop=center",
  
  // Bibles et livres
  bible1: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop&crop=center",
  bible2: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=300&fit=crop&crop=center",
  bible3: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=center",
  
  // Vatican et actualités religieuses
  vatican1: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop&crop=center", // Basilique Saint-Pierre 
  vatican2: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600&h=400&fit=crop&crop=center", // Architecture sacrée
  vatican3: "https://images.unsplash.com/photo-1578674240837-ac2b88b79c96?w=600&h=400&fit=crop&crop=center", // Art religieux
  
  // Images du Pape François et Vatican
  pope1: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=600&h=400&fit=crop&crop=center", // Figure papal moderne
  pope2: "https://images.unsplash.com/photo-1520208570826-30c5dcdfb973?w=600&h=400&fit=crop&crop=center", // Église contemporaine
  pope3: "https://images.unsplash.com/photo-1578674240837-ac2b88b79c96?w=600&h=400&fit=crop&crop=center", // Art religieux
  pope4: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop&crop=center", // Architecture Vatican
  popePortrait: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop&crop=faces", // Portrait religieux
  vaticanCity: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop&crop=center", // Cité du Vatican
  
  // Téléphones et applications
  phone: "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=300&fit=crop&crop=center",
  phone2: "https://images.unsplash.com/photo-1556656793-08538906a9f8?w=400&h=300&fit=crop&crop=center",
  
  // Images par défaut en cas de problème
  defaultPortrait: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=600&fit=crop&crop=faces",
  defaultLandscape: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop&crop=center",
  defaultSquare: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=400&fit=crop&crop=center"
};

export default ImageWithFallback;